import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccessDomain } from '../../models/AccessDomain';
import { Router } from '@angular/router';
import { Endpoints } from '@huge-monorepo/portal/config';
import { ToolsService } from '../tools/tools.service';
import { User } from 'libs/portal/src/models/User';
import { Routes } from '@huge-monorepo/portal/config';
import { DomainRoles, Domains } from 'libs/portal/src/models/DomainInfo';
import { NavigationService } from '../navigation/navigation.service';

export class LoginRequest {
  public Roles: UserAccessRole | null;
  public ID: number;
  public UserID: number;
  public UID: number;
  public Email: string;
  public Name: string;
  public Token: string;
  public GroupID: number;
  public GroupName: string;
  public KeepAliveAccess: boolean;
  public CDRAccess: boolean;
  public MikrotikAccess: boolean;
  public TeltonikaAccess: boolean;
  public ContractorsAccess: boolean;
  public RelayAccess: boolean;
  public UserRights: any[];
  public ImpersonatedUserRights?: any[];
  public ImpersonatedUser?: User;
  public HugeAdmin = false;
  public KeepAliveTypes: string[] = [];
  public SessionChanged = false;
  public isObfuscated = false;
  public DefaultGroupID?: number;
  public IsAggregated: boolean;
  public PortalReadAccess: boolean;
  public PortalWriteAccess: boolean;

  public SetUserAccessRoles = (roleObject: UserAccessRole | null) => {
    this.Roles = roleObject;
  };

  constructor(
    iD?: number,
    userID?: number,
    uID?: number,
    email?: string,
    name?: string,
    token?: string,
    groupID?: number,
    groupName?: string,
    keepAliveAccess?: boolean,
    cDRAccess?: boolean,
    mikrotikAccess?: boolean,
    relayAccess?: boolean,
    userRights?: any[],
    impersonatedUserRights?: any[],
    impersonatedUser?: User,
    hugeAdmin = false,
    keepAliveTypes: string[] = [],
    portalReadAccess: boolean = false,
    portalWriteAccess: boolean = false,
    sessionChanged = false,
    isObfuscated = false,
    defaultGroupID?: number,
    isAggregated?: boolean,
    teltonikaAccess?: boolean,
    contractorsAccess?: boolean,
  ) {
    this.ID = iD;
    this.UserID = userID;
    this.UID = uID;
    this.Email = email;
    this.Name = name;
    this.Token = token;
    this.GroupID = groupID;
    this.GroupName = groupName;
    this.KeepAliveAccess = keepAliveAccess;
    this.CDRAccess = cDRAccess;
    this.MikrotikAccess = mikrotikAccess;
    this.RelayAccess = relayAccess;
    this.TeltonikaAccess = teltonikaAccess;
    this.ContractorsAccess = contractorsAccess;
    this.UserRights = userRights;
    this.ImpersonatedUserRights = impersonatedUserRights;
    this.ImpersonatedUser = impersonatedUser;
    this.HugeAdmin = hugeAdmin;
    this.KeepAliveTypes = keepAliveTypes;
    this.SessionChanged = sessionChanged;
    this.isObfuscated = isObfuscated;
    this.DefaultGroupID = defaultGroupID;
    this.IsAggregated = isAggregated;
    this.PortalReadAccess = portalReadAccess;
    this.PortalWriteAccess = portalWriteAccess;
  }
}

export class AppErrorData {
  constructor(public URL: string, public Stack: string, public Data: Object, public Router: string) { }
}

@Injectable({
  providedIn: 'root',
})
export class UserAccessRole {
  // Mikrotik domain roles
  private isMikrotikReporting: boolean | null;
  private isMikrotikAdmin: boolean;
  protected isMikrotikUser: boolean;
  private isMikrotikGraphing: boolean;

  // CDR domain roles
  private isCDRReporting: boolean | null;
  private isCDRGraphing: boolean;
  private isCDRUser: boolean;
  private isCDRAdmin: boolean;

  // KeepAlive domain roles
  private isKeepAliveReporting: boolean | null;
  private isKeepAliveGraphing: boolean;
  private isKeepAliveAdmin: boolean;
  private isKeepAliveUser: boolean;

  // Relay domain roles
  private isRelayReporting: boolean | null;
  private isRelayGraphing: boolean;
  private isRelayUser: boolean;
  private isRelayAdmin: boolean;

  // Teltonika domain roles
  private isTeltonikaUser: boolean;
  private isTeltonikaAdmin: boolean;
  private isTeltonikaRemoteAccess: boolean;

  // Portal domain roles
  private isPortalUser: boolean;
  private isSuperUser: boolean | null;
  private isRegistered: boolean;
  private isReporting: boolean | null;

  //Contractors domain roles
  private isContractorsUser: boolean;

  public IsMikrotikReporting = () => {
    return this.isMikrotikReporting;
  };
  public IsMikrotikAdmin = () => {
    return this.isMikrotikAdmin;
  };
  public IsMikrotikUser = () => {
    return this.isMikrotikAdmin || this.isMikrotikUser;
  };
  public IsMikrotikGraphing = () => {
    return this.isMikrotikGraphing;
  };
  public HasMikrotikAccess = () => {
    return this.isSuperUser || this.isMikrotikAdmin || this.isMikrotikGraphing || this.isMikrotikUser;
  };

  public IsCDRReporting = () => {
    return this.isCDRReporting;
  };
  public IsCDRGraphing = () => {
    return this.isCDRGraphing;
  };
  public IsCDRUser = () => {
    return this.isCDRAdmin || this.isCDRUser;
  };
  public IsCDRAdmin = () => {
    return this.isCDRAdmin;
  };
  public HasCDRAccess = () => {
    return this.isSuperUser || this.isCDRAdmin || this.isCDRGraphing || this.isCDRUser;
  };

  public IsKeepAliveReporting = () => {
    return this.isKeepAliveReporting;
  };
  public IsKeepAliveGraphing = () => {
    return this.isKeepAliveGraphing;
  };
  public IsKeepAliveAdmin = () => {
    return this.isKeepAliveAdmin;
  };
  public IsKeepAliveUser = () => {
    return this.isKeepAliveAdmin || this.isKeepAliveUser;
  };
  public HasKeepAliveAccess = () => {
    return this.isSuperUser || this.isKeepAliveAdmin || this.isKeepAliveGraphing || this.isKeepAliveUser;
  };

  public IsRelayReporting = () => {
    return this.isRelayReporting;
  };
  public IsRelayGraphing = () => {
    return this.isRelayGraphing;
  };
  public IsRelayUser = () => {
    return this.isRelayAdmin || this.isRelayUser;
  };
  public IsRelayAdmin = () => {
    return this.isRelayAdmin;
  };
  public HasRelayAccess = () => {
    return this.isSuperUser || this.isRelayAdmin || this.isRelayGraphing || this.isRelayUser;
  };

  public IsTeltonikaUser = () => {
    return this.isTeltonikaAdmin || this.isTeltonikaUser;
  };
  public IsTeltonikaAdmin = () => {
    return this.isTeltonikaAdmin;
  };
    public IsTeltonikaRemoteAccess = () => {
    return this.isTeltonikaRemoteAccess;
  };
  public HasTeltonikaAccess = () => {
    return this.isSuperUser || this.isTeltonikaAdmin || this.isTeltonikaUser || this.isTeltonikaRemoteAccess;
  };

  public IsContractorsUser = () => {
    return this.isContractorsUser;
  }

  public HasContractorsAccess = () => {
    return this.isSuperUser || this.isContractorsUser;
  }

  public IsPortalUser = () => {
    return this.isSuperUser || this.isPortalUser;
  };
  public IsSuperUser = () => {
    return this.isSuperUser;
  };
  public IsRegistered = () => {
    return this.isRegistered;
  };
  public HasPortalAccess = () => {
    return this.isPortalUser || this.isSuperUser || this.isRegistered;
  };
  public IsReporting = () => {
    return this.isReporting;
  };

  constructor(authService: UserRightsService) {
    if (!authService?.HasAccess) {
      throw new Error('Access function not provided');
    }

    // Initialise the CDR domain roles
    this.isCDRReporting = authService.HasAccess(Domains.CDR_DOMAIN, DomainRoles.REPORTING_ROLES) || authService.IsHugeAdmin();
    this.isCDRGraphing = authService.HasAccess(Domains.CDR_DOMAIN, DomainRoles.GRAPHING_ROLES);
    this.isCDRUser = authService.HasAccess(Domains.CDR_DOMAIN, DomainRoles.USER_ROLES);
    this.isCDRAdmin = authService.HasAccess(Domains.CDR_DOMAIN, DomainRoles.ADMIN_ROLES);

    // Initialise the KeepAlive domain roles
    this.isKeepAliveReporting = authService.HasAccess(Domains.KEEPALIVE_DOMAIN, DomainRoles.REPORTING_ROLES) || authService.IsHugeAdmin();
    this.isKeepAliveGraphing = authService.HasAccess(Domains.KEEPALIVE_DOMAIN, DomainRoles.GRAPHING_ROLES);
    this.isKeepAliveUser = authService.HasAccess(Domains.KEEPALIVE_DOMAIN, DomainRoles.USER_ROLES);
    this.isKeepAliveAdmin = authService.HasAccess(Domains.KEEPALIVE_DOMAIN, DomainRoles.ADMIN_ROLES);

    // Initialise the Mikrotik domain roles
    this.isMikrotikReporting = authService.HasAccess(Domains.MIKROTIK_DOMAIN, DomainRoles.REPORTING_ROLES) || authService.IsHugeAdmin();
    this.isMikrotikGraphing = authService.HasAccess(Domains.MIKROTIK_DOMAIN, DomainRoles.GRAPHING_ROLES);
    this.isMikrotikUser = authService.HasAccess(Domains.MIKROTIK_DOMAIN, DomainRoles.USER_ROLES);
    this.isMikrotikAdmin = authService.HasAccess(Domains.MIKROTIK_DOMAIN, DomainRoles.ADMIN_ROLES);

    // Initialise the Relay domain roles
    this.isRelayReporting = authService.HasAccess(Domains.RELAY_DOMAIN, DomainRoles.REPORTING_ROLES) || authService.IsHugeAdmin();
    this.isRelayGraphing = authService.HasAccess(Domains.RELAY_DOMAIN, DomainRoles.GRAPHING_ROLES);
    this.isRelayUser = authService.HasAccess(Domains.RELAY_DOMAIN, DomainRoles.USER_ROLES);
    this.isRelayAdmin = authService.HasAccess(Domains.RELAY_DOMAIN, DomainRoles.ADMIN_ROLES);

    // Initialise the Teltonika domain roles
    this.isTeltonikaUser = authService.HasAccess(Domains.TELTONIKA_DOMAIN, DomainRoles.USER_ROLES);
    this.isTeltonikaAdmin = authService.HasAccess(Domains.TELTONIKA_DOMAIN, DomainRoles.ADMIN_ROLES);
    this.isTeltonikaRemoteAccess = authService.HasAccess(Domains.TELTONIKA_DOMAIN, DomainRoles.REMOTE_ACCESS_ROLES);

    // Initialise the Contractors domain roles
    this.isContractorsUser = authService.HasAccess(Domains.CONTRACTORS_DOMAIN, DomainRoles.USER_ROLES);

    // Initialise the portal domain roles
    this.isPortalUser = authService.HasAccess(Domains.CUSTOMER_DOMAIN, DomainRoles.USER_ROLES);
    this.isSuperUser = authService.HasAccess(Domains.CUSTOMER_DOMAIN, DomainRoles.SUPER_USER_ROLES) || authService.IsHugeAdmin();
    this.isRegistered = authService.HasAccess(Domains.CUSTOMER_DOMAIN, DomainRoles.REGISTERED_ROLES);
    this.isReporting = this.isMikrotikReporting || this.isKeepAliveReporting || this.isCDRReporting || this.isRelayReporting || authService.IsHugeAdmin();
  }
}

export class AppError {
  constructor(public Code: number, public Message: string, public APIMessage: string, public Data: AppErrorData, public Fatal: boolean = false) { }
}

@Injectable({
  providedIn: 'root',
})
export class UserRightsService {
  private session = window.sessionStorage;
  private local = window.localStorage;
  private RIGHTS = '';
  private RightsObject = new BehaviorSubject({});
  currentRightsObject = this.RightsObject.asObservable();

  private USER = '';
  private UserObject: BehaviorSubject<LoginRequest> | null;

  private ERROR = '';
  private ErrorObject: BehaviorSubject<AppError>;

  constructor(private router: Router, private endpoints: Endpoints, private tools: ToolsService, private routes: Routes, private navigationService: NavigationService) {
    this.setKeys();
    const rights = this.session.getItem(this.RIGHTS);
    if (rights != null) {
      this.RightsObject.next(JSON.parse(rights));
    }

    const user = this.session.getItem(this.USER);
    if (user != null) {
      let parsedUser = JSON.parse(user) as LoginRequest;
      if (parsedUser != null) {
        parsedUser = this.CopyLoginRequestData(parsedUser);
      }
      this.UserObject = new BehaviorSubject<LoginRequest>(parsedUser);
      this.UserObject.next(parsedUser); // Duplication necessary due to the dependency

      parsedUser.SetUserAccessRoles(new UserAccessRole(this));
      this.UserObject.next(parsedUser); // Duplication necessary due to the dependency
    }
  }

  CopyLoginRequestData(old: LoginRequest): LoginRequest {
    const newRequest = new LoginRequest();

    newRequest.ID = old.ID;
    newRequest.UserID = old.UserID;
    newRequest.UID = old.UID;
    newRequest.Email = old.Email;
    newRequest.Name = old.Name;
    newRequest.Token = old.Token;
    newRequest.GroupID = old.GroupID;
    newRequest.GroupName = old.GroupName;
    newRequest.KeepAliveAccess = old.KeepAliveAccess;
    newRequest.CDRAccess = old.CDRAccess;
    newRequest.MikrotikAccess = old.MikrotikAccess;
    newRequest.RelayAccess = old.RelayAccess;
    newRequest.TeltonikaAccess = old.TeltonikaAccess;
    newRequest.ContractorsAccess = old.ContractorsAccess;
    newRequest.UserRights = old.UserRights;
    newRequest.ImpersonatedUserRights = old.ImpersonatedUserRights;
    newRequest.ImpersonatedUser = old.ImpersonatedUser;
    newRequest.HugeAdmin = old.HugeAdmin;
    newRequest.KeepAliveTypes = old.KeepAliveTypes;
    newRequest.PortalReadAccess = old.PortalReadAccess;
    newRequest.PortalWriteAccess = old.PortalWriteAccess;
    newRequest.SessionChanged = old.SessionChanged;
    newRequest.isObfuscated = old.isObfuscated;
    newRequest.DefaultGroupID = old.DefaultGroupID;
    newRequest.IsAggregated = old.IsAggregated;
    newRequest.SetUserAccessRoles(old.Roles);

    return newRequest;
  }

  setKeys() {
    this.RIGHTS = this.endpoints.RIGHTS_KEY;
    this.USER = this.endpoints.USER_KEY;
    this.ERROR = this.endpoints.ERROR_KEY;
  }

  SetRights(values: AccessDomain[]) {
    if (values == null) {
      return;
    }

    const obj = {};
    values.forEach((domain) => {
      const roleObject = {};
      if (domain.Roles != null) {
        domain.Roles.forEach((role) => {
          roleObject[role.Description] = role;
        });
      }
      obj[domain.Description] = roleObject;
    });
    this.session.setItem(this.RIGHTS, JSON.stringify(obj));
    this.RightsObject.next(obj);
    this.currentRightsObject = this.RightsObject.asObservable();
  }

  GetUserAccessRoles(): UserAccessRole | null {
    if (!this.UserObject?.value) {
      return null;
    }

    return this.UserObject.value.Roles;
  }

  SetError(err: AppError) {
    if (this.ErrorObject == null) {
      this.ErrorObject = new BehaviorSubject<AppError>(err);
    }

    this.session.setItem(this.ERROR, JSON.stringify(err));
    this.ErrorObject.next(err);
  }

  GetError(): AppError | null {
    if (!this.ErrorObject) {
      return null;
    }

    return this.ErrorObject.value;
  }

  HasAccess(domain: string, roles: string[]): boolean {
    if (this.RightsObject == null) {
      return false;
    }

    if (this.RightsObject.value['Customer'] != null) {
      if (
        (this.RightsObject.value['Customer']['Super User'] != null && roles != DomainRoles.REPORTING_ROLES && roles != DomainRoles.GRAPHING_ROLES && !this.IsHugeAdmin()) ||
        (this.RightsObject.value['Customer']['Super User'] != null && this.IsHugeAdmin())
      ) {
        return true;
      } else if (this.RightsObject.value['Customer']['Registered'] != null) {
        return false;
      }
    }

    let valid = false;
    roles.forEach((role) => {
      if (this.RightsObject.value[domain] && this.RightsObject.value[domain][role]) {
        valid = true;
      }
    });
    return valid;
  }

  SetObfuscation(obfuscate: boolean) {
    const updatedUser = this.UserObject?.value;
    updatedUser.isObfuscated = obfuscate;
    this.session.setItem(this.USER, JSON.stringify(updatedUser));
    const user = JSON.parse(this.session.getItem(this.USER)) as LoginRequest;
    this.UserObject?.next(updatedUser);
  }

  ClearRights() {
    this.session.removeItem(this.RIGHTS);
    this.RightsObject = new BehaviorSubject({});
    this.currentRightsObject = this.RightsObject.asObservable();
  }

  HasReportingRole(): boolean {
    for (const key in this.RightsObject.value) {
      if (this.RightsObject.value[key]['Reporting'] != null || this.RightsObject.value[key]['Super User'] != null) {
        return true;
      }
    }
    return false;
  }

  IsLoggedIn() {
    if (this.UserObject != null) {
      if (this.UserObject.value.Token != null && this.UserObject.value.Token != '' && this.UserObject.value.UID > 0) {
        const expirationTime = this.tools.GetExpiryTimeFromToken(this.UserObject.value.Token);
        if (this.tools.GetTimeDifference(expirationTime) > 0) {
          return true;
        }
      }
    }
    return false;
  }

  UpdateToken(newToken: string) {
    if (this.UserObject != null) {
      this.UserObject.value.Token = newToken;
      this.session.setItem(this.USER, JSON.stringify(this.UserObject.value));
    }
  }

  GetToken(): string | null {
    if (this.UserObject != null && this.UserObject.value.Token) {
      return this.UserObject.value.Token;
    }
    return null;
  }

  calculateRefreshTime(): number {
    if (this.UserObject == null) {
      return 0;
    }
    const timeBefore = 30000; // 30 seconds in Milliseconds
    return this.tools.GetTimeDifference(this.tools.GetExpiryTimeFromToken(this.UserObject.value.Token)) - timeBefore;
  }

  GetUserID(): number | null {
    if (this.UserObject != null) {
      return this.UserObject.value.UserID;
    }
    return null;
  }

  GetUID(): number | null {
    if (this.UserObject != null) {
      return this.UserObject.value.UID;
    }
    return null;
  }

  GetUser(): BehaviorSubject<LoginRequest> | null {
    if (this.UserObject != null) {
      return this.UserObject;
    }
    return null;
  }

  GetUserPortalReadWrite(): UserPortalReadWrite | null {
    if (this.UserObject != null) {
      if (this.GetUserAccessRoles()?.IsSuperUser()) {
        return {
          PortalReadAccess: true,
          PortalWriteAccess: true,
        };
      }

      return {
        PortalReadAccess: this.UserObject.value.PortalReadAccess,
        PortalWriteAccess: this.UserObject.value.PortalWriteAccess,
      };
    }
    return null;
  }

  GetGroupID(): number | null {
    if (this.UserObject != null) {
      return this.UserObject.value.GroupID;
    }
    return null;
  }

  GetDefualtGroupID(): number | null {
    if (this.UserObject != null) {
      return this.UserObject.value.DefaultGroupID;
    }
    return null;
  }

  GetKeepAliveTypes(): string[] {
    if (this.UserObject != null) {
      return this.UserObject.value.KeepAliveTypes;
    }
    return [];
  }

  SetDefaultGroupID(id: number): void {
    if (this.UserObject != null) {
      const updatedUser = this.UserObject.value;
      updatedUser.DefaultGroupID = id;
      this.session.setItem(this.USER, JSON.stringify(updatedUser));
      this.UserObject.next(updatedUser);
    }
  }

  IsGroupAdmin(): boolean | null {
    if (this.RightsObject != null && this.RightsObject.value['Customer'] != null) {
      return this.RightsObject.value['Customer']['Super User'];
    }
    return null;
  }

  IsHugeAdmin(): boolean | null {
    if (this.UserObject != null) {
      return this.UserObject.value.HugeAdmin && !this.IsImpersonating();
    }
    return null;
  }

  HasKeepAliveAccess(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.KeepAliveAccess;
    }
    return false;
  }

  HasCDRAccess(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.CDRAccess;
    }
    return false;
  }

  HasMikrotikAccess(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.MikrotikAccess;
    }
    return false;
  }

  HasTeltonikaAccess(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.TeltonikaAccess;
    }
    return false;
  }

  HasContractorsAccess(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.ContractorsAccess;
    }
    return false;
  }

  HasRelayAccess(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.RelayAccess;
    }
    return false;
  }

  Logout() {
    this.ClearAccesses();
    localStorage.clear();
    this.navigationService.ClearState();  // 👈 clear nav state first
    this.router.navigate(['/']);
  }

  ClearAccesses() {
    sessionStorage.clear();
    this.ClearRights();
    this.UserObject = null;
    this.session.clear();
  }

  HasSessionChanged(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.SessionChanged;
    }
    return false;
  }

  IsAggregated(): boolean {
    if (this.UserObject != null) {
      return this.UserObject.value.IsAggregated;
    }
    return true;
  }

  SetSessionChanged(val: boolean) {
    if (this.UserObject != null) {
      this.UserObject.value.SessionChanged = val;
      this.session.setItem(this.USER, JSON.stringify(this.UserObject.value));
      this.SetRights(this.UserObject.value.UserRights);
    }
  }

  Login(request: LoginRequest): boolean {
    if (request == null || request.UID == 0 || request.Token == '') {
      return false;
    }

    this.session.setItem(this.USER, JSON.stringify(request));
    if (this.UserObject == null) {
      this.UserObject = new BehaviorSubject<LoginRequest>(request);
    }

    this.UserObject.next(request); // Duplication necessary due to the dependency
    request.SetUserAccessRoles(new UserAccessRole(this));
    this.UserObject.next(request); // Duplication necessary due to the dependency
    return true;
  }

  IsImpersonating(): User | null {
    if (this.UserObject != null) {
      return this.UserObject.value.ImpersonatedUser as any;
    }
    return null;
  }

  GetUserName(): string | null {
    if (this.UserObject != null && this.UserObject.value != null) {
      return this.UserObject.value.Name;
    }
    return null;
  }

  GetUserRights(): any[] | null {
    if (this.UserObject != null && this.UserObject.value != null) {
      return this.UserObject.value.UserRights;
    }
    return null;
  }

  GetGroupName(): string | null {
    if (this.UserObject != null && this.UserObject.value != null) {
      return this.UserObject.value.GroupName;
    }
    return null;
  }

  StartImpersonation(impersonationUser: User, impersonationUserRoles: any[]) {
    if (this.UserObject != null) {
      this.ClearRights();
      this.SetRights(impersonationUserRoles);

      const user = this.session.getItem(this.USER);
      if (user != null) {
        let parsedUser = JSON.parse(user) as LoginRequest;
        parsedUser.ImpersonatedUser = impersonationUser;
        parsedUser.ImpersonatedUserRights = impersonationUserRoles;
        parsedUser = this.CopyLoginRequestData(parsedUser);
        this.UserObject = new BehaviorSubject<LoginRequest>(parsedUser);
        parsedUser.SetUserAccessRoles(new UserAccessRole(this));
        this.UserObject.next(parsedUser); // Duplication necessary due to the dependency
        this.session.setItem(this.USER, JSON.stringify(parsedUser));
      }
      this.router.navigate(['/'], { skipLocationChange: true, replaceUrl: true });
    }
  }

  StopImpersonation() {
    if (this.UserObject != null && this.UserObject.value != null) {
      this.ClearRights();
      this.SetRights(this.UserObject.value.UserRights);

      const user = this.session.getItem(this.USER);
      if (user != null) {
        let parsedUser: any = JSON.parse(user) as LoginRequest;
        parsedUser.ImpersonatedUser = null;
        parsedUser.ImpersonatedUserRights = null;
        parsedUser = this.CopyLoginRequestData(parsedUser);
        this.UserObject = new BehaviorSubject<LoginRequest>(parsedUser);
        parsedUser.SetUserAccessRoles(new UserAccessRole(this));
        this.UserObject.next(parsedUser);
        this.session.setItem(this.USER, JSON.stringify(parsedUser));
      }

      this.router.navigate(['/'], { skipLocationChange: true, replaceUrl: true });
    }
  }
}

export class UserPortalReadWrite {
  PortalReadAccess: boolean;
  PortalWriteAccess: boolean;
}
